import { useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Loader2, Upload } from "lucide-react";
import {
  SidebarProvider,
  SidebarInset,
} from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppSidebar } from "@/components/dashboard/AppSidebar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AssetCard } from "@/components/assets/AssetCard";
import { createAssetsApi } from "@/lib/api/assets";
import type { Asset, AssetType } from "@/lib/types/assets";

type TypeFilter = "all" | AssetType;

const TYPE_TABS: { label: string; value: TypeFilter }[] = [
  { label: "All", value: "all" },
  { label: "Images", value: "image" },
  { label: "Audio", value: "audio" },
  { label: "Video", value: "video" },
  { label: "Documents", value: "document" },
];

export function AssetsPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [renameAsset, setRenameAsset] = useState<Asset | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteAsset, setDeleteAsset] = useState<Asset | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const assetsApi = createAssetsApi({ getToken });

  const { data: assets = [], isLoading, error } = useQuery({
    queryKey: ["assets", typeFilter, search],
    queryFn: () =>
      assetsApi.list({
        type: typeFilter === "all" ? undefined : typeFilter,
        q: search || undefined,
      }),
    staleTime: 60_000,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => assetsApi.upload(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      assetsApi.rename(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setRenameAsset(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      setDeleteAsset(null);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => uploadMutation.mutate(file));
    e.target.value = "";
  };

  const handleRenameOpen = (asset: Asset) => {
    setRenameAsset(asset);
    setRenameValue(asset.name);
  };

  const handleRenameSubmit = () => {
    if (!renameAsset || !renameValue.trim()) return;
    renameMutation.mutate({ id: renameAsset.id, name: renameValue.trim() });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => uploadMutation.mutate(file));
  };

  return (
    <TooltipProvider>
      <SidebarProvider className="h-svh min-h-0! overflow-hidden">
        <AppSidebar />
        <SidebarInset className="max-h-svh overflow-hidden bg-surface-raised">
          <div
            className="relative flex h-svh flex-col overflow-y-auto"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Drag overlay */}
            {isDragOver && (
              <div className="pointer-events-none absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-brand/60 bg-brand/5">
                <Upload className="h-10 w-10 text-brand/70" />
                <p className="text-base font-medium text-brand/80">Drop files to upload</p>
              </div>
            )}

            {/* Header */}
            <div className="sticky top-0 z-10 backdrop-blur">
              <div className="flex h-16 items-center justify-between px-4 md:px-6">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold">Assets</h1>
                </div>
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMutation.isPending}
                  className="gap-1.5 cursor-pointer"
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,audio/*,video/*,.pdf,.txt,.md"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </div>

              {/* Filter bar */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 md:px-6 pb-4">
                <div className="relative w-full sm:max-w-xs">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search assets..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 bg-muted/50 border-border/50"
                  />
                </div>
                <div className="flex items-center gap-1 overflow-x-auto">
                  {TYPE_TABS.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setTypeFilter(tab.value)}
                      className={`shrink-0 cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        typeFilter === tab.value
                          ? "bg-foreground text-background"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 px-4 md:px-6 py-6">
              {error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-destructive">Failed to load assets</p>
                  <p className="text-muted-foreground text-sm mt-2">
                    {error instanceof Error ? error.message : "Unknown error"}
                  </p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : assets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                  <p className="text-muted-foreground font-medium">No assets yet</p>
                  <p className="text-muted-foreground/60 text-sm max-w-xs">
                    Upload images, audio, video, or documents to reuse them across your pipelines.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 gap-1.5 cursor-pointer"
                  >
                    <Upload className="h-4 w-4" />
                    Upload files
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {assets.map((asset) => (
                    <AssetCard
                      key={asset.id}
                      asset={asset}
                      onRename={handleRenameOpen}
                      onDelete={setDeleteAsset}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>

      {/* Rename dialog */}
      <Dialog open={!!renameAsset} onOpenChange={(open) => !open && setRenameAsset(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename asset</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleRenameSubmit()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameAsset(null)} className="cursor-pointer">
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              disabled={renameMutation.isPending || !renameValue.trim()}
              className="cursor-pointer"
            >
              {renameMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteAsset} onOpenChange={(open) => !open && setDeleteAsset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete asset?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteAsset?.name}&rdquo; will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAsset && deleteMutation.mutate(deleteAsset.id)}
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
